import React, { type ReactNode } from "react";
import { Tab } from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import { TextColumn } from "ui/text";
import { ProgressBar } from "./progress-bar";

export type ProgressTabProps = {
  /**
   * The color theme of the tab content.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * Determines if the tab is active. Active tabs have a progress bar.
   */
  isActive?: boolean;

  /**
   * The title of the tab. Hidden on mobile screens.
   */
  title: string;

  /**
   * The description of the tab. Hidden on mobile screens.
   */
  description: string;

  /**
   * The media displayed when the tab is active
   */
  media: ReactNode;

  /**
   * Progress bar duration in seconds.
   *
   * @default 5
   */
  progressDuration?: number;

  /**
   * A function that is called when the progress bar ends.
   */
  onProgressEnd?: () => void;
};

/**
 * ProgressTab is a tab that displays a progress bar when active.
 *
 * On mobile screens, only the progress bar is displayed, and the title and description are hidden.
 */
export function ProgressTab({
  isActive,
  theme = "light",
  progressDuration = 5,
  onProgressEnd,
  ...props
}: ProgressTabProps) {
  return (
    <Tab display="flex" alignItems="start" flex={1} flexDir="column" gap="tiny" textAlign="left" p={0}>
      <ProgressBar
        isActive={isActive}
        theme={theme}
        progressDuration={progressDuration}
        onProgressEnd={onProgressEnd}
      />
      <TextColumn theme={theme} display={{ base: "none", md: "flex" }} {...props} />
    </Tab>
  );
}
